import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import type { Me, Rights } from '../lib/session.js';
import { BrandSelect } from '../lib/brands.js';
import { ENV_META } from '../lib/environments.js';
import { dateTime, num } from '../lib/format.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
  Tile,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import { Check, RunModes, Steps, activeEnv, duration } from '../components/2c-parts.js';
import type { StepState } from '../components/2c-parts.js';
import { SeLists } from './SeLists.js';
import type { ListRow, Selection } from './SeLists.js';
import { SeAfterImport } from './SeAfterImport.js';
import { SeSentHistory } from './SeSentHistory.js';
import { SeRecommended } from './SeRecommended.js';
import '../screens-2c.css';

export interface Run {
  id: string;
  brand: string;
  mode: 'dry_run' | 'apply';
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
  phase: string;
  counts: Record<string, number>;
  report: {
    onlySe?: Array<{ email: string; blacklisted: boolean; unsubscribed: boolean }>;
    onlyHub?: Array<{ email: string; contactId: string }>;
    conflicts?: Array<{ email: string; contactId: string; reason: string }>;
    differences?: Array<{ email: string; contactId: string; fields: Array<'name' | 'phone'> }>;
    sample?: Array<{
      email: string;
      blacklisted: boolean;
      hardbounced: boolean;
      lists: number;
      unsubscribed: boolean;
      customFields: number;
      hasName: boolean;
      hasPhone: boolean;
    }>;
    limited?: boolean;
    historyWindow?: { from: string; to: string };
    historyLimitReached?: boolean;
    affectedBrands?: string[];
  };
  progress?: {
    phases: Array<{
      phase: string;
      done: number;
      total: number;
      perSec: number | null;
      remainingSec: number | null;
    }>;
    elapsedSec: number;
    remainingSec: number | null;
  };
  error: string | null;
  errors: Array<{ phase: string; message: string }>;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  options: {
    consentListIds: number[];
    consentPurposeIds: number[];
    createContacts: boolean;
    consentFromEcomail?: boolean;
    history: boolean;
    emails?: 'all' | 'latest' | 'skip';
    emailsLatest?: number;
    maxContacts?: number;
    listIds?: number[];
    catalogOnly?: boolean;
    sentOnly?: boolean;
  };
}
interface Catalog {
  lists: ListRow[];
  purposes: Array<{ extId: string; name: string; members: number; lawfulBasis: string | null }>;
}

const PHASE_LABEL: Record<string, string> = {
  catalog: 'Seznamy, pole a účely',
  emails: 'E-maily',
  contacts: 'Kontakty',
  unsubscribed: 'Odhlášení ze seznamů',
  purposes: 'Souhlasy (účely)',
  match: 'Spárování a zápis',
  events: 'Historie reakcí',
  sent: 'Odeslané e-maily',
  done: 'Hotovo',
};
const STATUS: Record<Run['status'], { label: string; tone: Tone }> = {
  queued: { label: 'čeká na spuštění', tone: 'neu' },
  running: { label: 'běží', tone: 'acc' },
  done: { label: 'hotovo', tone: 'ok' },
  failed: { label: 'selhal', tone: 'bad' },
  cancelled: { label: 'zrušen', tone: 'warn' },
};
const COUNT_LABEL: Record<string, string> = {
  lists: 'Seznamů',
  listsCounted: 'Seznamů s načtenými počty',
  customFields: 'Vlastních polí',
  unsubscribedMarked: 'Označeno jako odhlášené ve SmartEmailingu',
  purposes: 'Účelů',
  emails: 'E-mailů ve SmartEmailingu',
  emailsHub: 'z toho nahraných hubem',
  contacts: 'Kontaktů ve SmartEmailingu',
  blacklisted: 'Blokovaných',
  unsubscribedListed: 'Odhlášení ze seznamů',
  matched: 'Spárováno s hubem',
  onlySe: 'Jen ve SmartEmailingu',
  onlyHub: 'Jen v hubu',
  conflicts: 'Konflikty (odhlášený ve SmartEmailingu, souhlas v hubu)',
  differs: 'Jméno nebo telefon se liší (hub × SmartEmailing)',
  profileFilled: 'Doplněno jméno, telefon nebo firma',
  customFieldOptions: 'Voleb vlastních polí',
  removedFromMirror: 'Odebráno z kopie (už nejsou ve SmartEmailingu)',
  wouldCreate: 'Založilo by se kontaktů',
  wouldGrant: 'Souhlas by dostalo',
  created: 'Založeno kontaktů',
  blacklistedSet: 'Označeno jako blokované',
  withdrawn: 'Souhlas odvolán',
  granted: 'Souhlas udělen',
  events: 'Reakcí staženo',
  eventsStored: 'z toho nových',
  eventsDuplicate: 'duplicit',
  historyCampaigns: 'Kampaní ke čtení reakcí',
  historyCampaignErrors: 'Kampaní s chybou',
};

const EMAILS_HELP: Record<'all' | 'latest' | 'skip', string> = {
  skip: 'Nestahuje se žádný e-mail ze SmartEmailingu (nejrychlejší); jen se spárují šablony nahrané hubem.',
  latest:
    'Stáhne jen nejnovější e-maily (nejnovější mají nejvyšší číslo); šablony nahrané hubem se doplní vždy.',
  all: 'Stáhne všechny e-maily účtu; SmartEmailing je vydává po 10, takže u tisíců e-mailů trvá desítky minut.',
};

export function SeImport({ me, rights }: { me: Me; rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const canWrite = rights.write('settings');
  const [brand, setBrand] = useState('TGP');
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<Selection>({ listIds: [], consentListIds: [] });
  const [selectionLoaded, setSelectionLoaded] = useState<string | null>(null);
  const [wholeAccount, setWholeAccount] = useState(false);
  const [purposes, setPurposes] = useState<number[]>([]);
  const [history, setHistory] = useState(true);
  const [create, setCreate] = useState(true);
  const [ecomailConsent, setEcomailConsent] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [emails, setEmails] = useState<'all' | 'latest' | 'skip'>('skip');
  const [emailsLatest, setEmailsLatest] = useState('200');
  const [maxContacts, setMaxContacts] = useState('');
  const [asking, setAsking] = useState(false);
  const maxValue = Number.parseInt(maxContacts, 10);
  const latestValue = Number(emailsLatest);
  const latestInvalid =
    emails === 'latest' &&
    !(Number.isInteger(latestValue) && latestValue >= 1 && latestValue <= 5000);
  const maxInvalid = maxContacts.trim() !== '' && !(maxValue >= 1);

  const runs = useQuery<{ items: Run[] }>({
    queryKey: ['se-import', 'runs', brand],
    queryFn: () => api.get(`/api/provider-import/runs${qs({ brand })}`),
    refetchInterval: (q) =>
      q.state.data?.items.some((r) => r.status === 'running' || r.status === 'queued')
        ? 4000
        : false,
  });
  const catalog = useQuery<Catalog>({
    queryKey: ['se-import', 'catalog', brand],
    queryFn: () => api.get(`/api/provider-import/catalog${qs({ brand })}`),
  });
  const saved = useQuery<Selection>({
    queryKey: ['se-import', 'selection', brand],
    queryFn: () => api.get(`/api/provider-import/selection${qs({ brand })}`),
    staleTime: Infinity,
  });
  // Poslední výběr značky se načte jednou; dál platí lokální stav (ukládá se níže).
  useEffect(() => {
    if (saved.data && selectionLoaded !== brand) {
      setSelection(saved.data);
      setSelectionLoaded(brand);
    }
  }, [saved.data, brand, selectionLoaded]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const changeSelection = (next: Selection): void => {
    setSelection(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api
        .put('/api/provider-import/selection', { brand, ...next })
        .catch((err: Error) => setError(err.message));
    }, 500);
  };
  const refresh = (): void => void client.invalidateQueries({ queryKey: ['se-import'] });
  const fail = (err: Error): void => setError(err.message);
  const loadLists = useMutation({
    mutationFn: () =>
      api.post('/api/provider-import/runs', {
        brand,
        mode: 'dry_run',
        options: { catalogOnly: true },
      }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });
  const start = useMutation({
    mutationFn: (input: { mode: 'dry_run' | 'apply'; word?: string }) =>
      api.post('/api/provider-import/runs', {
        brand,
        mode: input.mode,
        confirmation: input.word,
        options: {
          consentListIds: selection.consentListIds,
          consentPurposeIds: purposes,
          history,
          createContacts: create,
          consentFromEcomail: ecomailConsent,
          listIds: wholeAccount ? [] : selection.listIds,
          emails,
          ...(emails === 'latest' && !latestInvalid ? { emailsLatest: latestValue } : {}),
          ...(maxContacts.trim() !== '' && maxValue >= 1 ? { maxContacts: maxValue } : {}),
        },
      }),
    onSuccess: () => {
      setError(null);
      setAsking(false);
      refresh();
    },
    onError: (err: Error) => {
      setAsking(false);
      fail(err);
    },
  });
  const act = useMutation({
    mutationFn: (input: { id: string; action: 'cancel' | 'resume' }) =>
      api.post(`/api/provider-import/runs/${input.id}/${input.action}`),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  if (runs.isLoading) return <LoadingState what="importy" />;
  if (runs.isError)
    return (
      <ErrorState
        error={runs.error}
        title="Importy se nepodařilo načíst"
        onRetry={() => void runs.refetch()}
      />
    );
  const all = runs.data?.items ?? [];
  const active = all.find((r) => r.status === 'queued' || r.status === 'running');
  const catalogRun = all.find((r) => r.options.catalogOnly);
  const items = all.filter((r) => !r.options.catalogOnly && !r.options.sentOnly);
  const sentRuns = all.filter((r) => r.options.sentOnly);
  const shown = items.find((r) => r.id === open) ?? items[0];
  const lists = catalog.data?.lists ?? [];
  const noSelection = !wholeAccount && selection.listIds.length === 0;
  const catalogProgress = catalogRun?.progress?.phases.find((x) => x.phase === 'catalog');
  const toggle = (set: number[], setter: (v: number[]) => void, id: number): void =>
    setter(set.includes(id) ? set.filter((x) => x !== id) : [...set, id]);
  const dryDone = items.some((r) => r.mode === 'dry_run' && r.status === 'done');
  const applyDone = items.some((r) => r.mode === 'apply' && r.status === 'done');
  const step = (done: boolean, now: boolean): StepState => (done ? 'done' : now ? 'now' : 'todo');
  const picked = lists.length > 0 && !noSelection;
  const envName = ENV_META[activeEnv(me)].label;
  const blockStart = Boolean(active) || maxInvalid || latestInvalid || noSelection;
  const blockReason = active
    ? 'Pro tuhle značku už import běží.'
    : noSelection
      ? 'Nejdřív vyberte seznamy v kroku 1 nebo zaškrtněte celý účet.'
      : maxInvalid || latestInvalid
        ? 'Opravte hodnoty označené jako chybné.'
        : undefined;

  return (
    <>
      <PageHeader
        level={2}
        title="Import ze SmartEmailingu"
        description="Přečte seznamy a kontakty ze SmartEmailingu a uloží je do hubu. Jen čtení: do SmartEmailingu se při importu nic nezapisuje a žádný e-mail se neodešle."
      />
      <Steps
        label="Postup importu ze SmartEmailingu"
        steps={[
          {
            title: 'Seznamy',
            text: 'Načtěte seznamy a vyberte ty, jejichž kontakty chcete.',
            state: step(picked, true),
          },
          {
            title: 'Zkouška nanečisto',
            text: 'Uvidíte, co by se stalo. V hubu se nic nezmění.',
            state: step(dryDone, picked),
          },
          {
            title: 'Kontrola počtů',
            text: 'Projděte souhrn: kolik kontaktů vznikne, komu se odvolá souhlas.',
            state: step(applyDone, dryDone),
          },
          {
            title: 'Ostrý běh',
            text: 'Kontakty a souhlasy se zapíšou do hubu.',
            state: step(applyDone, dryDone),
          },
        ]}
      />
      <RunModes
        dry="Hub přečte vybrané seznamy a spočítá, co by se stalo: kolik kontaktů by vzniklo, komu by se odvolal souhlas. V hubu se nic nezmění, takže zkoušku můžete opakovat."
        live={`Totéž a navíc zapíše kontakty, odvolané souhlasy a blokace do databáze prostředí ${envName}. Do SmartEmailingu se stále nezapisuje nic.`}
      />
      <div className="help">
        <p>
          Kontakty, které hub nezná, se zakládají <b>bez souhlasu</b>; souhlas vznikne jen tam, kde
          to výslovně vyberete. Odhlášený nebo blokovaný kontakt souhlas nikdy nedostane.
        </p>
      </div>
      <Panel title="Značka" subtitle="Import se vždy spouští za jednu značku.">
        <div className="s2c-form">
          <Field label="Značka">
            <BrandSelect value={brand} onChange={setBrand} allLabel={null} />
          </Field>
        </div>
        {error ? <ErrorState error={new Error(error)} title="Akce se nepovedla" /> : null}
      </Panel>

      <Panel
        title="1. Seznamy"
        subtitle="Stáhne seznamy a vše, co o nich SmartEmailing prozradí (odesílatel, kategorie, počty členů). Kontakty se nestahují."
        action={
          canWrite ? (
            <button
              type="button"
              className="btn pri"
              disabled={Boolean(active) || loadLists.isPending}
              onClick={() => loadLists.mutate()}
            >
              {lists.length > 0 ? 'Načíst seznamy znovu' : 'Načíst seznamy'}
            </button>
          ) : undefined
        }
      >
        {catalogRun ? (
          <p className="s2c-runstate" role="status">
            Poslední načtení: {dateTime(catalogRun.createdAt, me.defaultTimezone)}
            <StatusBadge tone={STATUS[catalogRun.status].tone}>
              {STATUS[catalogRun.status].label}
            </StatusBadge>
            {catalogRun.status === 'running' && catalogProgress
              ? `počty seznamů ${num(catalogProgress.done)} z ${num(catalogProgress.total)}`
              : ''}
            {catalogRun.status === 'done' && catalogRun.counts.lists
              ? `seznamů ${num(catalogRun.counts.lists)}, polí ${num(catalogRun.counts.customFields ?? 0)}`
              : ''}
            {catalogRun.error ? catalogRun.error : ''}
          </p>
        ) : null}
        <p className="note">
          Počty členů (celkem, potvrzení, odhlášení) se zjišťují jedním lehkým dotazem na každý
          seznam, takže načtení stovky seznamů trvá jen chvíli. Seznam, u kterého počet nevyšel,
          ukazuje „—“.
        </p>
        {catalog.isLoading ? <LoadingState what="seznamy" /> : null}
        {catalog.isError ? (
          <ErrorState
            error={catalog.error}
            title="Seznamy se nepodařilo načíst"
            onRetry={() => void catalog.refetch()}
          />
        ) : null}
        {lists.length === 0 && !catalog.isLoading && !catalog.isError ? (
          <EmptyState title="Seznamy ještě nejsou načtené" icon="upload">
            Klikněte na „Načíst seznamy“. Z účtu se přečtou jen názvy a počty, žádné kontakty.
          </EmptyState>
        ) : (
          <SeLists
            lists={lists}
            selection={selection}
            onChange={changeSelection}
            canWrite={canWrite}
            tz={me.defaultTimezone}
          />
        )}
      </Panel>

      <Panel
        title="2. Import vybraných seznamů"
        subtitle="Nejdřív nanečisto, potom ostře. Čte se jen z vybraných seznamů."
      >
        {canWrite ? (
          <>
            <Check
              checked={wholeAccount}
              onChange={setWholeAccount}
              label="Importovat celý účet"
              hint="Bez výběru seznamů; u velkého účtu trvá dlouho."
            />
            {noSelection ? (
              <p className="note" role="status">
                Vyberte aspoň jeden seznam v kroku 1, nebo zaškrtněte celý účet.
              </p>
            ) : (
              <p className="note">
                {wholeAccount
                  ? 'Čte se celý účet.'
                  : `Vybraných seznamů: ${num(selection.listIds.length)}. Kontakt ve více seznamech se uloží jednou.`}{' '}
                Seznamů dokládajících souhlas: {num(selection.consentListIds.length)} (potvrzení
                členové dostanou souhlas).
              </p>
            )}

            <h3 className="s2c-sub">Zkouška na malé množině</h3>
            <div className="s2c-form">
              <Field
                label="Nejvýš kontaktů (strop pro zkoušku)"
                hint="Než stáhnete všechno, zkuste 10 až 50 kontaktů. Omezený běh zapisuje jen vybrané kontakty a historii reakcí nestahuje."
                error={maxInvalid ? 'Zadejte celé číslo od 1, nebo pole nechte prázdné.' : null}
              >
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  className="s2c-num"
                  placeholder="bez omezení"
                  value={maxContacts}
                  onChange={(e) => setMaxContacts(e.target.value)}
                  aria-invalid={maxInvalid}
                />
              </Field>
            </div>

            {catalog.data && catalog.data.purposes.length > 0 ? (
              <fieldset className="s2c-fieldset">
                <legend>Účely, které znamenají marketingový souhlas (volitelné)</legend>
                {catalog.data.purposes.map((p) => (
                  <Check
                    key={p.extId}
                    checked={purposes.includes(Number(p.extId))}
                    onChange={() => toggle(purposes, setPurposes, Number(p.extId))}
                    label={p.name}
                    hint={`${num(p.members)} členů${p.lawfulBasis ? ` · ${p.lawfulBasis}` : ''}`}
                  />
                ))}
              </fieldset>
            ) : null}

            <h3 className="s2c-sub">E-maily ve SmartEmailingu</h3>
            <div className="s2c-form">
              <Field label="Které e-maily stáhnout" hint={EMAILS_HELP[emails]}>
                <select
                  value={emails}
                  onChange={(e) => setEmails(e.target.value as 'all' | 'latest' | 'skip')}
                >
                  <option value="skip">Vynechat</option>
                  <option value="latest">Jen nejnovějších</option>
                  <option value="all">Všechny</option>
                </select>
              </Field>
              {emails === 'latest' ? (
                <Field
                  label="Kolik nejnovějších e-mailů (1 až 5000)"
                  error={latestInvalid ? 'Zadejte celé číslo od 1 do 5000.' : null}
                >
                  <input
                    type="number"
                    min={1}
                    max={5000}
                    step={1}
                    inputMode="numeric"
                    className="s2c-num"
                    value={emailsLatest}
                    onChange={(e) => setEmailsLatest(e.target.value)}
                    aria-invalid={latestInvalid}
                  />
                </Field>
              ) : null}
            </div>

            <h3 className="s2c-sub">Co s kontakty udělat</h3>
            <Check
              checked={create}
              onChange={setCreate}
              label="Zakládat kontakty, které hub nezná"
              hint="Vzniknou bez souhlasu, takže marketing nedostanou. Po vypnutí se jen spárují existující."
            />
            <Check
              checked={ecomailConsent}
              onChange={setEcomailConsent}
              label="Souhlas z Ecomail štítků „-subscribed“"
              hint="Členové těchto štítků dostanou marketingový souhlas se zdrojem ecomail_subscribed (rozhodnutí majitele, D-328), pokud nejsou odhlášení ani blokovaní a hub o souhlasu nic neví. Webové opt-iny, zpravodaje, „all contacts“ a „Ecomail investigace“ souhlas nedávají."
            />
            <Check
              checked={history}
              onChange={setHistory}
              label="Stáhnout historii reakcí"
              hint="Až 12 měsíců otevření a kliknutí. Čte se po kampaních (SmartEmailing jinak dotaz odmítá); newslettery a automatizace SmartEmailing nevypisuje, jejich reakce chybí. Jen ostrý běh bez omezení počtu kontaktů."
            />

            <div className="s2c-actions">
              <button
                type="button"
                className="btn pri"
                disabled={blockStart || start.isPending}
                onClick={() => start.mutate({ mode: 'dry_run' })}
              >
                Importovat nanečisto
              </button>
              <button
                type="button"
                className="btn dng"
                disabled={blockStart}
                onClick={() => setAsking(true)}
              >
                Importovat ostře
              </button>
              {blockReason ? <span className="s2c-reason">{blockReason}</span> : null}
            </div>
          </>
        ) : (
          <p className="note">Import může spustit jen správce.</p>
        )}
      </Panel>

      <SeSentHistory
        brand={brand}
        runs={sentRuns}
        active={Boolean(active)}
        canWrite={canWrite}
        tz={me.defaultTimezone}
        envName={envName}
      />

      {shown ? (
        <Panel
          title={`Běh ${shown.mode === 'dry_run' ? 'nanečisto' : 'ostrý'} z ${dateTime(shown.createdAt, me.defaultTimezone)}`}
          subtitle={
            shown.mode === 'dry_run'
              ? 'Zkouška: v hubu se nic nezměnilo.'
              : 'Ostrý běh: výsledky jsou zapsané v hubu.'
          }
          action={
            <StatusBadge tone={STATUS[shown.status].tone}>{STATUS[shown.status].label}</StatusBadge>
          }
        >
          {shown.status === 'running' || shown.status === 'queued' ? (
            <p className="s2c-runstate" role="status">
              Běh probíhá, stránka se sama obnovuje. Můžete ji nechat otevřenou nebo se vrátit
              později.
            </p>
          ) : null}
          <p className="note">
            Fáze: <b>{PHASE_LABEL[shown.phase] ?? shown.phase}</b>
            {shown.counts.contacts ? ` · kontaktů ${num(shown.counts.contacts)}` : ''}
          </p>
          <p className="note">
            Volby: e-maily{' '}
            {shown.options.emails === 'skip'
              ? 'vynechány'
              : shown.options.emails === 'all'
                ? 'všechny'
                : `nejnovějších ${num(shown.options.emailsLatest ?? 200)}`}
            {shown.options.maxContacts
              ? ` · kontaktů nejvýš ${num(shown.options.maxContacts)}`
              : ''}
          </p>
          {shown.report.historyWindow ? (
            <p className="note">
              Historie reakcí se čte po kampaních, okno{' '}
              {dateTime(shown.report.historyWindow.from, me.defaultTimezone)} až{' '}
              {dateTime(shown.report.historyWindow.to, me.defaultTimezone)}
              {shown.report.historyLimitReached ? ' · dosažen strop počtu reakcí' : ''}.
            </p>
          ) : null}
          {shown.errors.some((e) => e.phase === 'events') ? (
            <ErrorState
              error={
                new Error(
                  shown.errors
                    .filter((e) => e.phase === 'events')
                    .map((e) => e.message)
                    .join('; '),
                )
              }
              title="Reakce některých kampaní se nepodařilo přečíst"
            />
          ) : null}
          {shown.progress?.phases.length ? (
            <div>
              {shown.progress.phases.map((x) => (
                <div key={x.phase} className="s2c-phase">
                  <span>
                    {PHASE_LABEL[x.phase] ?? x.phase}: {num(x.done)}
                    {x.total > 0 ? ` z ${num(x.total)}` : ''}
                    {x.perSec ? ` · ${x.perSec.toLocaleString('cs-CZ')} položek/s` : ''}
                    {x.remainingSec ? ` · zbývá asi ${duration(x.remainingSec)}` : ''}
                  </span>
                  {x.total > 0 ? (
                    <progress
                      max={x.total}
                      value={x.done}
                      aria-label={`Průběh: ${PHASE_LABEL[x.phase] ?? x.phase}`}
                    />
                  ) : null}
                </div>
              ))}
              <p className="note">
                Uplynulo {duration(shown.progress.elapsedSec)}
                {shown.status === 'running' && shown.progress.remainingSec !== null
                  ? ` · odhad zbývajícího času u fází se známým rozsahem: ${duration(shown.progress.remainingSec)}`
                  : ''}
                . Odhad se zpřesňuje s během a další fáze (kontakty, historie) se do něj započítají,
                až se zjistí jejich rozsah.
              </p>
            </div>
          ) : null}
          {shown.error ? (
            <ErrorState error={new Error(shown.error)} title="Běh skončil chybou" />
          ) : null}
          <div className="s2c-actions">
            {canWrite && (shown.status === 'running' || shown.status === 'queued') ? (
              <button
                type="button"
                className="btn"
                onClick={() => act.mutate({ id: shown.id, action: 'cancel' })}
              >
                Zrušit běh
              </button>
            ) : null}
            {canWrite && (shown.status === 'failed' || shown.status === 'cancelled') ? (
              <button
                type="button"
                className="btn pri"
                onClick={() => act.mutate({ id: shown.id, action: 'resume' })}
              >
                Pokračovat od místa přerušení
              </button>
            ) : null}
          </div>
          <div className="tiles">
            {Object.entries(shown.counts).map(([key, value]) => (
              <Tile
                key={key}
                label={COUNT_LABEL[key] ?? key}
                value={num(value)}
                bad={key === 'conflicts' && value > 0}
              />
            ))}
          </div>
          {shown.report.limited ? (
            <p className="note">
              Zkušební běh:{' '}
              {shown.options.maxContacts ? `nejvýš ${num(shown.options.maxContacts)} kontaktů` : ''}
              {shown.options.listIds?.length
                ? ` · jen vybrané seznamy (${shown.options.listIds.length})`
                : ''}
              . „Jen v hubu“ se nepočítá a historie reakcí se nestahuje.
            </p>
          ) : null}
          {shown.report.sample?.length ? (
            <ReportTable
              title="Vzorek zkontrolovaných kontaktů (maskovaný)"
              rows={shown.report.sample.map((c) => [
                c.email,
                c.blacklisted ? 'blokovaný' : c.hardbounced ? 'nedoručitelný' : 'v pořádku',
                c.unsubscribed ? 'odhlášený z některého seznamu' : '—',
                String(c.lists),
                String(c.customFields),
                `${c.hasName ? 'jméno' : '—'} · ${c.hasPhone ? 'telefon' : '—'}`,
              ])}
              head={['Příjemce', 'Stav', 'Odhlášení', 'Seznamů', 'Vlastních polí', 'Vyplněno']}
              note="Pět kontaktů pro porovnání se SmartEmailingem; jména ani telefony se tu neukazují."
            />
          ) : null}
          {shown.report.differences?.length ? (
            <ReportTable
              title="Jméno nebo telefon se liší mezi hubem a SmartEmailingem"
              rows={shown.report.differences.map((c) => [
                c.email,
                c.fields.map((f) => (f === 'name' ? 'jméno' : 'telefon')).join(', '),
              ])}
              head={['Příjemce', 'Liší se']}
              note="Import hodnoty z e-shopu nepřepisuje; SmartEmailing jen doplní prázdná pole."
            />
          ) : null}
          {shown.report.conflicts?.length ? (
            <ReportTable
              title="Konflikty: odhlášený nebo blokovaný ve SmartEmailingu, ale souhlas v hubu"
              rows={shown.report.conflicts.map((c) => [
                c.email,
                c.reason === 'blacklisted' ? 'blokovaný' : 'odhlášený',
              ])}
              head={['Příjemce', 'Ve SmartEmailingu']}
              note="Ostrý běh u nich souhlas odvolá."
            />
          ) : null}
          {shown.report.onlySe?.length ? (
            <ReportTable
              title="Jen ve SmartEmailingu (hub je nezná)"
              rows={shown.report.onlySe.map((c) => [
                c.email,
                c.blacklisted ? 'blokovaný' : c.unsubscribed ? 'odhlášený' : '—',
              ])}
              head={['Příjemce', 'Příznak']}
              note="Zakládají se bez souhlasu."
            />
          ) : null}
          {shown.report.onlyHub?.length ? (
            <ReportTable
              title="Jen v hubu (SmartEmailing je nezná)"
              rows={shown.report.onlyHub.map((c) => [c.email, 'v hubu'])}
              head={['Příjemce', 'Kde je']}
              note="Nic se s nimi neděje; marketing jim jde z hubu přes šablonu bez ohledu na seznamy ve SmartEmailingu."
            />
          ) : null}
        </Panel>
      ) : (
        <EmptyState title="Import zatím neběžel" icon="upload">
          Načtěte seznamy, vyberte je a spusťte import nanečisto. Nic se tím nezmění.
        </EmptyState>
      )}

      {shown && shown.status === 'done' ? (
        <SeAfterImport run={shown} canSeeCustomers={rights.read('customers')} />
      ) : null}

      {items.length > 1 ? (
        <Panel title="Dřívější běhy" subtitle="Kliknutím zobrazíte výsledky daného běhu.">
          <ul className="s2c-runlist">
            {items.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className="btn"
                  aria-pressed={r.id === shown?.id}
                  onClick={() => setOpen(r.id)}
                >
                  {dateTime(r.createdAt, me.defaultTimezone)} ·{' '}
                  {r.mode === 'dry_run' ? 'nanečisto' : 'ostrý'} · {STATUS[r.status].label}
                </button>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      <ConfirmDialog
        open={asking}
        title="Importovat ze SmartEmailingu ostře?"
        confirmLabel="Importovat ostře"
        danger
        requireWord="IMPORT"
        busy={start.isPending}
        onCancel={() => setAsking(false)}
        onConfirm={(word) => start.mutate({ mode: 'apply', word })}
      >
        <p>
          Do databáze prostředí <b>{envName}</b> se zapíšou kontakty, odvolané souhlasy a blokace ze
          SmartEmailingu. Do SmartEmailingu se nezapisuje nic a žádný e-mail se neodešle.
        </p>
        <p>
          {wholeAccount
            ? 'Čte se celý účet.'
            : `Vybraných seznamů: ${num(selection.listIds.length)}.`}{' '}
          {dryDone
            ? 'Zkouška nanečisto už proběhla.'
            : 'Zkouška nanečisto zatím neproběhla; doporučujeme ji pustit jako první.'}
        </p>
      </ConfirmDialog>
      <SeRecommended brand={brand} canWrite={canWrite} />
    </>
  );
}

function ReportTable({
  title,
  head,
  rows,
  note,
}: {
  title: string;
  head: string[];
  rows: string[][];
  note: string;
}): JSX.Element {
  return (
    <div>
      <h3 className="s2c-sub">{title}</h3>
      <p className="note">
        {note} Zobrazeno prvních {rows.length}; adresy jsou maskované.
      </p>
      <div className="scroll">
        <table>
          <thead>
            <tr>
              {head.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {r.map((c, j) => (
                  <td key={j}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
